"use client";

import { useMemo, useState } from "react";
import { Frame, Slider, Verdict, Note } from "./parts";

const L = {
    fr: {
        title: "Le moins cher dépend de votre panier moyen",
        amount: "Montant de la vente",
        cheapest: "Le moins cher ici",
        kept: "Vous gardez",
        crossover: "Les deux se valent à",
        note: "Les deux grilles sont fictives et choisies pour montrer le mécanisme : une commission fixe élevée est indolore sur les gros montants et dévastatrice sur les petits. Comparez vos prestataires avec vos propres chiffres, sur votre panier moyen réel.",
        smallTicket: "Sur les petits montants, la commission fixe domine : c'est la grille sans frais fixe qui gagne, même avec un pourcentage plus élevé.",
        bigTicket: "Sur les gros montants, le pourcentage domine et la commission fixe devient négligeable : le classement s'inverse.",
        nearCross: "Vous êtes tout près du point où les deux grilles se valent. Un déplacement de votre panier moyen change le gagnant.",
        aName: "Grille A",
        bName: "Grille B",
        aDesc: "2,9 % + 0,25 €",
        bDesc: "3,4 % sans frais fixe",
        fee: "Commission",
    },
    en: {
        title: "The cheapest one depends on your average order",
        amount: "Sale amount",
        cheapest: "Cheapest here",
        kept: "You keep",
        crossover: "The two are equal at",
        note: "Both rate cards are fictional and chosen to show the mechanism: a high fixed fee is painless on large amounts and devastating on small ones. Compare your processors with your own figures, at your real average order value.",
        smallTicket: "On small amounts the fixed fee dominates: the card with no fixed fee wins, even at a higher percentage.",
        bigTicket: "On large amounts the percentage dominates and the fixed fee becomes negligible: the ranking reverses.",
        nearCross: "You are close to the point where both cards are equal. A shift in your average order changes the winner.",
        aName: "Card A",
        bName: "Card B",
        aDesc: "2.9% + €0.25",
        bDesc: "3.4% with no fixed fee",
        fee: "Fee",
    },
};

// Deux grilles réalistes mais fictives. Le croisement tombe à 0,25 / (0,034 - 0,029)
// soit 50 €, une valeur que beaucoup de paniers moyens encadrent.
const A = { pct: 0.029, fixed: 0.25 };
const B = { pct: 0.034, fixed: 0 };
const CROSS = A.fixed / (B.pct - A.pct);

export default function ProcessorFees({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [amount, setAmount] = useState(18);

    const r = useMemo(() => {
        const feeA = amount * A.pct + A.fixed;
        const feeB = amount * B.pct + B.fixed;
        return {
            feeA,
            feeB,
            best: feeA <= feeB ? "a" : "b",
            near: Math.abs(amount - CROSS) / CROSS < 0.18,
        };
    }, [amount]);

    const euro = (v) => `${v.toFixed(2).replace(".", lang === "fr" ? "," : ".")} €`;
    const pctOf = (fee) => `${((fee / amount) * 100).toFixed(1)} %`;

    const card = (key, name, desc, fee) => {
        const winner = r.best === key;
        return (
            <div
                className={`rounded-xl p-4 border ${winner ? "border-primary/40 bg-primary/[0.06] dark:bg-primary/10" : "border-gray-200/70 dark:border-white/10"}`}
            >
                <div className="flex justify-between items-baseline gap-3">
                    <span className={`font-bold ${winner ? "text-primary" : "dark:text-white"}`}>{name}</span>
                    <span className="text-xs text-gray-500 dark:text-lightBlue/70">{desc}</span>
                </div>
                <div className="flex justify-between items-baseline gap-3 mt-2">
                    <span className="text-sm text-gray-600 dark:text-lightBlue">{t.fee}</span>
                    <span className="text-lg font-bold tabular-nums dark:text-white">
                        {euro(fee)}
                        <span className="text-sm font-normal text-gray-500 dark:text-lightBlue/70"> · {pctOf(fee)}</span>
                    </span>
                </div>
                <div className="flex justify-between items-baseline gap-3">
                    <span className="text-sm text-gray-600 dark:text-lightBlue">{t.kept}</span>
                    <span className="text-sm font-bold tabular-nums dark:text-white">{euro(amount - fee)}</span>
                </div>
            </div>
        );
    };

    return (
        <Frame icon="calculator-minimalistic-linear" title={t.title}>
            <div className="max-w-sm mb-5">
                <Slider label={t.amount} value={amount} onChange={setAmount} min={2} max={300} step={1} display={euro(amount)} />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                {card("a", t.aName, t.aDesc, r.feeA)}
                {card("b", t.bName, t.bDesc, r.feeB)}
            </div>

            <div className="rounded-xl bg-gray-100/70 dark:bg-white/[0.04] p-4 mt-3 flex justify-between items-baseline gap-3">
                <span className="text-sm text-gray-600 dark:text-lightBlue">{t.crossover}</span>
                <span className="text-lg font-bold tabular-nums dark:text-white">{euro(CROSS)}</span>
            </div>

            <Verdict tone={r.near ? "warn" : "neutral"}>
                {r.near ? t.nearCross : amount < CROSS ? t.smallTicket : t.bigTicket}
            </Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
